import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { trackOperation } from './trackOperation';

/**
 * Creates a normalized query name for Sentry tracking by removing specific IDs
 * @param queryKey The query key array
 * @returns A normalized query name for grouping similar queries
 */
function getNormalizedQueryName(queryKey: unknown[]): string {
  if (!queryKey.length) return 'empty';

  // Create a normalized version of the query key
  return queryKey
    .map((part, index) => {
      // Keep the first part (usually the resource name) as is
      if (index === 0) return String(part);

      // Check if the part is an ID-like value (number or string that looks like an ID)
      if (
        typeof part === 'number' ||
        (typeof part === 'string' && /^[0-9a-f-]+$/.test(part))
      ) {
        return '{id}';
      }

      // For objects, return a placeholder
      if (typeof part === 'object' && part !== null) {
        return '{params}';
      }

      // Otherwise, keep the part as is
      return String(part);
    })
    .join(':');
}

// Wrapper for useQuery with Sentry performance tracking
export function useTrackedQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData
>(
  queryKey: unknown[],
  queryFn: () => Promise<TQueryFnData>,
  options?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<TData, TError> {
  // Get both the full and normalized query names
  const fullQueryName = Array.isArray(queryKey)
    ? queryKey.join(':')
    : String(queryKey);
  const normalizedQueryName = getNormalizedQueryName(
    Array.isArray(queryKey) ? queryKey : [queryKey]
  );

  // Wrap the queryFn with our generic Sentry span tracker
  // Use the normalized name for the span, but track the full name as metadata
  const trackedQueryFn = trackOperation(
    queryFn,
    `query:${normalizedQueryName}`,
    'query',
    {
      query_key: fullQueryName,
      query_key_normalized: normalizedQueryName,
    }
  );

  // Use the original useQuery with our wrapped function
  return useQuery<TQueryFnData, TError, TData>(
    queryKey,
    trackedQueryFn,
    options
  );
}
